import { useCallback, useState } from 'react'
import BackofficePanel, { PanelError, Row } from '../BackofficePanel'
import { useAdminPoll } from '../../../app/backoffice/useAdminPoll'
import {
  deleteUser,
  getAdminActivity,
  getAdminLeagues,
  getAdminUser,
  getAdminUsers,
  getEngagement,
  purgeMockFriends,
  renameUser,
} from '../../../api/admin'
import { serverErrorMessage } from '../../../api/client'
import { isDevToolAvailable } from '../../../devToolCapabilities'
import type { DataMode } from '../../../dataMode'
import type { AdminUserDetail, AdminUserView } from '../../../adminApi'

/**
 * People & engagement.
 *
 * The headline number is the point: *"142 of 180 users have submitted predictions for GW12."*
 * Before a deadline that is the only operational metric anyone wants, and it is also the number
 * that tells you something is broken before the complaints arrive.
 *
 * Read-mostly, and two writes now. The first is a rename — an offensive display name sits on every
 * leaderboard in every league until someone can change it, and before this panel nobody could. The
 * second is the deletion: when somebody asks for their account and their data to be
 * erased, the answer used to be SQL by hand.
 */

const ENGAGEMENT_POLL_MS = 30_000
const SLOW_POLL_MS = 120_000

/** What was actually removed, for the toast — the same per-table counts the audit row carries. */
function summariseTables(tables: Record<string, number>): string {
  const { leagues_disowned: disowned = 0, ...deleted } = tables
  const touched = Object.entries(deleted).filter(([, rows]) => rows > 0)
  const total = touched.reduce((sum, [, rows]) => sum + rows, 0)
  const detail = touched.map(([table, rows]) => `${table} ${rows}`).join(', ')
  const ownerless = disowned > 0 ? ` · ${disowned} league(s) left ownerless` : ''
  return `${total} rows across ${touched.length} tables (${detail})${ownerless}`
}

export default function PeoplePanel({
  enabled,
  mode,
  onToast,
}: {
  enabled: boolean
  /** Which world we are in — the deletion row is capability-gated on it, like every other write. */
  mode: DataMode
  onToast: (message: string) => void
}) {
  const loadEngagement = useCallback(() => getEngagement(), [])
  const engagement = useAdminPoll(loadEngagement, { enabled, intervalMs: ENGAGEMENT_POLL_MS })

  const [search, setSearch] = useState('')
  const loadUsers = useCallback(() => getAdminUsers(search || undefined), [search])
  const users = useAdminPoll(loadUsers, { enabled, intervalMs: SLOW_POLL_MS })

  const loadLeagues = useCallback(() => getAdminLeagues(), [])
  const leagues = useAdminPoll(loadLeagues, { enabled, intervalMs: SLOW_POLL_MS })

  const loadActivity = useCallback(() => getAdminActivity(), [])
  const activity = useAdminPoll(loadActivity, { enabled, intervalMs: SLOW_POLL_MS })

  const [selected, setSelected] = useState<AdminUserDetail | null>(null)

  const open = async (row: AdminUserView) => {
    try {
      setSelected(await getAdminUser(row.id))
    } catch (failure) {
      onToast(`Could not load ${row.displayName}: ${failure instanceof Error ? failure.message : failure}`)
    }
  }

  const rename = async (row: AdminUserView) => {
    const next = window.prompt(`New display name for ${row.displayName}`, row.displayName)
    if (!next) return
    // The reason is required server-side too; asking for it here means the refusal never happens.
    const reason = window.prompt('Why are you renaming this user?')
    if (!reason) return
    try {
      await renameUser(row.id, next, reason)
      users.refresh()
      onToast(`Renamed to ${next}.`)
    } catch (failure) {
      onToast(`Rename refused: ${failure instanceof Error ? failure.message : String(failure)}`)
    }
  }

  /**
   * Delete a user and every row they own.
   *
   * Two prompts, in this order: the typed display name first, then the reason. Both are validated
   * here so the server's 400 never has to happen — it is the backstop, not the UX — and the typed
   * name is keyed to *this* user, so a stale sheet cannot delete the wrong person.
   */
  const remove = async (row: AdminUserView) => {
    const typed = window.prompt(
      `Delete ${row.displayName} and every row they own. Their predictions, points, chips, ` +
        'quest picks, season history, league memberships and rivalries all go, and it cannot be ' +
        `undone.\n\nType the display name to confirm:`
    )
    if (typed === null) return
    if (typed !== row.displayName) {
      onToast(`Not deleted — expected "${row.displayName}".`)
      return
    }
    const reason = window.prompt('Why is this account being deleted?')
    if (!reason?.trim()) return
    try {
      const result = await deleteUser(row.id, typed, reason.trim())
      setSelected(null)
      users.refresh()
      engagement.refresh()
      leagues.refresh()
      onToast(`Deleted ${row.displayName}: ${summariseTables(result.tables)}`)
    } catch (failure) {
      // Verbatim: each refusal names what to do next, and "failed" names nothing.
      onToast(serverErrorMessage(failure))
    }
  }

  /**
   * Purge all 10 mock-friend bots at once — the action the per-row Delete refuses
   * to do one bot at a time. Same two-prompt shape as `remove`, but the typed phrase is fixed
   * rather than keyed to a target, since there is no single row to key it to.
   */
  const purgeBots = async () => {
    const typed = window.prompt(
      'Delete all 10 mock-friend bots and everything they own: predictions, chips, league ' +
        "memberships and the admin's rivalries with them. It cannot be undone, and they will not " +
        'come back on the next restart.\n\nType "DELETE ALL BOTS" to confirm:'
    )
    if (typed === null) return
    if (typed !== 'DELETE ALL BOTS') {
      onToast('Not purged — expected "DELETE ALL BOTS".')
      return
    }
    const reason = window.prompt('Why are the bots being purged?')
    if (!reason?.trim()) return
    try {
      const result = await purgeMockFriends(reason.trim())
      users.refresh()
      engagement.refresh()
      leagues.refresh()
      onToast(
        result.purged === 0
          ? 'No bots left to purge.'
          : `Purged ${result.purged} bot(s): ${summariseTables(result.tables)}`
      )
    } catch (failure) {
      onToast(serverErrorMessage(failure))
    }
  }

  return (
    <BackofficePanel
      title="People"
      badge={engagement.data ? `${engagement.data.predicted}/${engagement.data.users}` : '…'}
      defaultOpen={false}
    >
      {engagement.error !== null && <PanelError error={engagement.error} />}
      {engagement.data && (
        <>
          <div className="bo-stat">
            <span className="bo-stat__value">
              {engagement.data.predicted}/{engagement.data.users}
            </span>
            <span className="bo-stat__label">
              users have predicted for GW{engagement.data.gameweek}
            </span>
          </div>
          {/* Complete vs partial: partial is what the auto-fill exists for, and knowing how
              many it will fire for is the thing to check *before* a deadline, not after. */}
          <Row label="complete" value={String(engagement.data.complete)} />
          <Row label="partial (auto-fill will fire)" value={String(engagement.data.partial)} />
          <Row label="no predictions" value={String(engagement.data.none)} />
          <Row label="already auto-filled" value={String(engagement.data.autoFilled)} />
          <Row label="banker picked" value={String(engagement.data.bankerPicked)} />
          <Row label="quest picked" value={String(engagement.data.questPicked)} />
          <Row
            label="chips active"
            value={`DU ${engagement.data.chipsActive.doubleUp} · CS ${engagement.data.chipsActive.cleanSweep} · GF ${engagement.data.chipsActive.goalfest}`}
          />
          {/* Counted separately, never folded in: the 10 bots predict every gameweek, and adding
              them to a participation rate makes every number a lie. */}
          <Row label="bots (excluded above)" value={String(engagement.data.bots)} />
          {isDevToolAvailable('adminPurgeMockFriends', mode) && (
            <button type="button" className="bo-btn bo-btn--danger" onClick={() => void purgeBots()}>
              Purge mock-friend bots
            </button>
          )}
        </>
      )}

      <div className="bo-group">
        <p className="bo-group__title">Users</p>
        <input
          className="bo-input"
          value={search}
          placeholder="Search name, email or id"
          onChange={(e) => setSearch(e.target.value)}
        />
        {users.error !== null && <PanelError error={users.error} />}
        <div className="bo-list">
          {(users.data?.users ?? []).slice(0, 100).map((row) => (
            <div key={row.id} className="bo-feed-row">
              <span className="bo-feed-row__text">
                {row.displayName}
                {row.isBot && <span className="bo-tag"> bot</span>}
                {row.isAdmin && <span className="bo-tag"> admin</span>}
                <br />
                <span className="bo-mono">{row.email ?? row.id}</span>
              </span>
              <span className="bo-mono">{row.totalPoints}</span>
              <button type="button" className="bo-btn" onClick={() => void open(row)}>
                Open
              </button>
              <button type="button" className="bo-btn" onClick={() => void rename(row)}>
                Rename
              </button>
            </div>
          ))}
        </div>
      </div>

      {selected && (
        <div className="bo-group">
          <p className="bo-group__title">
            {selected.user.displayName}
            <button type="button" className="bo-btn" onClick={() => setSelected(null)}>
              close
            </button>
          </p>
          <Row label="joined" value={selected.user.joinedAt ?? 'unknown'} />
          <Row label="last active" value={selected.user.lastActiveAt ?? 'never'} />
          <Row label="leagues" value={selected.leagues.map((league) => league.name).join(', ') || '—'} />
          <Row label="rivals" value={String(selected.rivals.length)} />
          {/* Enough to answer "my points are wrong" from this screen, without a query tool. */}
          {selected.history.map((entry) => (
            <Row
              key={entry.gameweek}
              label={`GW${entry.gameweek}`}
              value={`${entry.points} pts · ${entry.predicted} predicted (${entry.autoFilled} auto)`}
            />
          ))}

          {/* Below everything, in its own danger block — never a fourth neutral button beside the
              rename. Gated on the capability like every other write control. */}
          {isDevToolAvailable('adminDeleteUser', mode) && (
            <div className="bo-danger">
              <p className="bo-danger__title">Delete account</p>
              <p className="bo-reason">
                Removes every row this user owns, in one transaction. The audit trail and any
                fixture correction they made stay; a league they created keeps running for its
                other members, ownerless.
              </p>
              <button
                type="button"
                className="bo-btn bo-btn--danger"
                onClick={() => void remove(selected.user)}
              >
                Delete {selected.user.displayName} and all their data
              </button>
            </div>
          )}
        </div>
      )}

      <div className="bo-group">
        <p className="bo-group__title">Leagues</p>
        {leagues.error !== null && <PanelError error={leagues.error} />}
        {leagues.data && (
          <>
            <Row label="total" value={String(leagues.data.total)} />
            {/* Auto-generated club leagues behave differently from user-created ones; mixing them
                makes both numbers useless. */}
            <Row label="system (global + club)" value={String(leagues.data.system)} />
            <Row label="user-created" value={String(leagues.data.userCreated)} />
            {leagues.data.sizeBuckets.map((bucket) => (
              <Row key={bucket.label} label={`${bucket.label} members`} value={String(bucket.count)} />
            ))}
            {leagues.data.largest.slice(0, 5).map((league) => (
              <Row key={league.id} label={league.name} value={`${league.members} members`} />
            ))}
          </>
        )}
      </div>

      <div className="bo-group">
        <p className="bo-group__title">Activity</p>
        {activity.error !== null && <PanelError error={activity.error} />}
        {activity.data && (
          <>
            <Row label="sessions active (24h)" value={String(activity.data.activeSessions24h)} />
            {activity.data.signupsByDay.slice(-7).map((day) => (
              <Row key={day.day} label={`signups ${day.day}`} value={String(day.count)} />
            ))}
          </>
        )}
      </div>
    </BackofficePanel>
  )
}
